"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { parseWholeNumberInput } from "@/lib/tool-kit/number-words/whole-number";

const RE_ENDS_IN_Y = /y$/;

const ONES = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
];

const TENS = [
  "",
  "",
  "twenty",
  "thirty",
  "forty",
  "fifty",
  "sixty",
  "seventy",
  "eighty",
  "ninety",
];

const SCALES = ["", "thousand", "million", "billion", "trillion"];

const ORDINAL_WORDS: Record<string, string> = {
  zero: "zeroth",
  one: "first",
  two: "second",
  three: "third",
  five: "fifth",
  eight: "eighth",
  nine: "ninth",
  twelve: "twelfth",
};

function convertHundreds(n: number): string {
  if (n < 20) {
    return ONES[n];
  }
  if (n < 100) {
    const ten = TENS[Math.floor(n / 10)];
    const one = n % 10;
    return one === 0 ? ten : `${ten}-${ONES[one]}`;
  }
  const hundred = `${ONES[Math.floor(n / 100)]} hundred`;
  const remainder = n % 100;
  if (remainder === 0) {
    return hundred;
  }
  return `${hundred} ${convertHundreds(remainder)}`;
}

function numberToWords(n: number): string {
  if (n === 0) {
    return "zero";
  }
  const chunks: number[] = [];
  let remaining = n;
  while (remaining > 0) {
    chunks.push(remaining % 1000);
    remaining = Math.floor(remaining / 1000);
  }
  const parts: string[] = [];
  for (let i = chunks.length - 1; i >= 0; i--) {
    if (chunks[i] === 0) {
      continue;
    }
    const words = convertHundreds(chunks[i]);
    parts.push(SCALES[i] ? `${words} ${SCALES[i]}` : words);
  }
  return parts.join(" ");
}

function ordinalizeWord(word: string): string {
  const irregular = ORDINAL_WORDS[word];
  if (irregular !== undefined) {
    return irregular;
  }
  if (RE_ENDS_IN_Y.test(word)) {
    return `${word.slice(0, -1)}ieth`;
  }
  return `${word}th`;
}

function toOrdinalWords(n: number): string {
  const words = numberToWords(n);
  const lastBreak = Math.max(words.lastIndexOf(" "), words.lastIndexOf("-"));
  const prefix = words.slice(0, lastBreak + 1);
  const last = words.slice(lastBreak + 1);
  return prefix + ordinalizeWord(last);
}

function ordinalSuffix(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) {
    return "th";
  }
  const mod10 = n % 10;
  if (mod10 === 1) {
    return "st";
  }
  if (mod10 === 2) {
    return "nd";
  }
  if (mod10 === 3) {
    return "rd";
  }
  return "th";
}

interface OrdinalResult {
  input: string;
  suffixForm: string;
  words: string;
}

export function convertOne(raw: string): OrdinalResult | null {
  const n = parseWholeNumberInput(raw.trim());
  if (n === null || n < 0 || !Number.isSafeInteger(n)) {
    return null;
  }
  return {
    input: String(n),
    suffixForm: `${n}${ordinalSuffix(n)}`,
    words: toOrdinalWords(n),
  };
}

const SUFFIX_RULE_IDS = [
  "last1",
  "last2",
  "last3",
  "teens",
  "everythingElse",
] as const;

const SUFFIX_RULE_DATA: Record<
  (typeof SUFFIX_RULE_IDS)[number],
  { suffix: string; examples: string }
> = {
  last1: { suffix: "st", examples: "1st, 21st, 101st" },
  last2: { suffix: "nd", examples: "2nd, 22nd, 142nd" },
  last3: { suffix: "rd", examples: "3rd, 33rd, 103rd" },
  teens: { suffix: "th", examples: "11th, 12th, 113th" },
  everythingElse: { suffix: "th", examples: "4th, 9th, 20th, 100th" },
};

type Mode = "single" | "batch";

/** English strings. A translated content file overrides these through `ui`. */
export const ORDINAL_NUMBER_CONVERTER_STRINGS = {
  mode: "Mode",
  singleNumber: "Single number",
  batchOnePerLine: "Batch (one number per line)",
  enterWholeNumber: "Enter a whole number",
  inputPlaceholder: "22",
  ordinalSuffixFormLabel: "Ordinal (suffix form)",
  copied: "Copied!",
  copy: "Copy",
  ordinalSpelledOutLabel: "Ordinal (spelled out)",
  singleErrorMessage: "Enter a whole number 0 or greater, like 22 or 1,000.",
  onePerLine: "One number per line",
  batchPlaceholder: "1\n2\n11\n22\n103",
  results: "Results",
  copyAll: "Copy All",
  notAWholeNumber: " → (not a whole number)",
  arrow: " → ",
  emDash: " — ",
  suffixRulesHeading: "Ordinal suffix rules",
  ruleColumn: "Rule",
  suffixColumn: "Suffix",
  examplesColumn: "Examples",
  ruleLabels: {
    last1: "Last digit is 1 (not 11)",
    last2: "Last digit is 2 (not 12)",
    last3: "Last digit is 3 (not 13)",
    teens: "Ends in 11, 12, or 13",
    everythingElse: "Everything else",
  },
  teensExceptionNote:
    'The teens exception is the one everyone trips over: 11, 12, and 13 always take "th" because they are pronounced eleventh, twelfth, thirteenth — so 111th and 112th are correct, while 21st and 22nd follow the last-digit rule.',
} as const;

export default function OrdinalNumberConverterWidget() {
  const [mode, setMode] = useState<Mode>("single");
  const [input, setInput] = useState("");
  const [batchInput, setBatchInput] = useState("");
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(ORDINAL_NUMBER_CONVERTER_STRINGS);

  const single = input.trim().length > 0 ? convertOne(input) : null;
  const showSingleError = input.trim().length > 0 && single === null;

  const batchLines = batchInput
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  const batchResults = batchLines.map((line) => ({
    line,
    result: convertOne(line),
  }));
  const batchText = batchResults
    .map((r) => {
      if (r.result === null) {
        return `${r.line}${strings.notAWholeNumber}`;
      }
      return `${r.line}${strings.arrow}${r.result.suffixForm}${strings.emDash}${r.result.words}`;
    })
    .join("\n");

  function handleCopy(value: string) {
    writeToClipboard(value).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.mode}</p>
        <div className="flex flex-wrap gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "single"}
              className="accent-primary"
              name="ordinal-mode"
              onChange={() => setMode("single")}
              type="radio"
              value="single"
            />
            {strings.singleNumber}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "batch"}
              className="accent-primary"
              name="ordinal-mode"
              onChange={() => setMode("batch")}
              type="radio"
              value="batch"
            />
            {strings.batchOnePerLine}
          </label>
        </div>
      </section>

      {mode === "single" && (
        <section className="space-y-3">
          <div className="space-y-2">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="ordinal-input"
            >
              {strings.enterWholeNumber}
            </label>
            <input
              className="w-full rounded-lg border border-border bg-card p-4 text-foreground text-lg leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              id="ordinal-input"
              inputMode="numeric"
              onChange={(e) => setInput(e.target.value)}
              placeholder={strings.inputPlaceholder}
              type="text"
              value={input}
            />
          </div>
          {single !== null && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 p-4">
                <div>
                  <p className="text-muted-foreground text-xs">
                    {strings.ordinalSuffixFormLabel}
                  </p>
                  <p className="font-semibold text-2xl text-foreground">
                    {single.suffixForm}
                  </p>
                </div>
                <button
                  className="rounded-lg border border-border bg-card px-4 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted"
                  onClick={() => handleCopy(single.suffixForm)}
                  type="button"
                >
                  {copied ? strings.copied : strings.copy}
                </button>
              </div>
              <div className="rounded-lg border border-border bg-card p-4">
                <p className="text-muted-foreground text-xs">
                  {strings.ordinalSpelledOutLabel}
                </p>
                <p className="text-foreground text-lg">{single.words}</p>
              </div>
            </div>
          )}
          {showSingleError && (
            <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
              {strings.singleErrorMessage}
            </p>
          )}
        </section>
      )}

      {mode === "batch" && (
        <section className="space-y-3">
          <div className="space-y-2">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="ordinal-batch-input"
            >
              {strings.onePerLine}
            </label>
            <textarea
              className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              id="ordinal-batch-input"
              onChange={(e) => setBatchInput(e.target.value)}
              placeholder={strings.batchPlaceholder}
              value={batchInput}
            />
          </div>
          {batchResults.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="font-medium text-foreground text-sm">
                  {strings.results}
                </p>
                <button
                  className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground"
                  onClick={() => handleCopy(batchText)}
                  type="button"
                >
                  {copied ? strings.copied : strings.copyAll}
                </button>
              </div>
              <textarea
                className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
                readOnly
                value={batchText}
              />
            </div>
          )}
        </section>
      )}

      {/* Suffix rules */}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.suffixRulesHeading}
        </h2>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-border border-b bg-muted/30">
                <th className="p-3 font-medium text-foreground">
                  {strings.ruleColumn}
                </th>
                <th className="p-3 font-medium text-foreground">
                  {strings.suffixColumn}
                </th>
                <th className="p-3 font-medium text-foreground">
                  {strings.examplesColumn}
                </th>
              </tr>
            </thead>
            <tbody>
              {SUFFIX_RULE_IDS.map((id) => (
                <tr className="border-border border-b last:border-b-0" key={id}>
                  <td className="p-3 text-muted-foreground">
                    {strings.ruleLabels[id]}
                  </td>
                  <td className="p-3 font-mono text-foreground">
                    {SUFFIX_RULE_DATA[id].suffix}
                  </td>
                  <td className="p-3 text-muted-foreground">
                    {SUFFIX_RULE_DATA[id].examples}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.teensExceptionNote}
        </p>
      </section>
    </>
  );
}
